// Helpers de contactos de proyecto y de tipo de subsidio.
//
// El parseo de cg_12 es el mismo que hace el backend
// (server_obras_v2/src/services/contactosProyectoService.js). Se repite a
// proposito: el backend normaliza al guardar, pero el frontend tiene que
// mostrar bien los datos que YA estan en la base, que pueden ser de antes de
// la migracion 062. Si solo trusteara al backend, las 154 obras con objeto
// unico y las 29 con texto plano no se verian.

// -----------------------------------------------------------------------------
// Tipo de subsidio
//
// cg_03_tipo_subsidio tiene DOS capitalizaciones en produccion: 437 obras con
// "Subsidio a municipio" y 34 con "Subsidio a Municipio". Los options del
// select usan la minuscula, asi que esas 34 no matchean contra nada y el
// efecto que resetea el organismo no corre. Es un bug preexistente; se
// resuelve comparando sin case y sin acentos en vez de tocar los options, que
// si se tocaran dejarian 437 obras sin matchear en el otro sentido.
// -----------------------------------------------------------------------------
const normalizarTexto = (s) => String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // saca acentos
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export const esTipoSubsidio = (tipo, valor) => normalizarTexto(tipo) === normalizarTexto(valor);

// Los 4 valores canonicos de optionsTipoSubsidio.
export const normalizarTipoSubsidio = (tipo) => {
    const t = normalizarTexto(tipo);
    if (t.includes('municipio')) return 'Subsidio a municipio';
    if (t.includes('ong')) return 'Subsidio a ONG';
    if (t.includes('universidad')) return 'Subsidio a Universidades';
    if (t === 'otro') return 'Otro';
    return tipo || '';
};

// Solo las obras de ONG tienen contactos de proyecto. Es la misma regla que
// aplica el backend al resolver el catalogo.
export const esObraConONG = (tipo) => esTipoSubsidio(tipo, 'Subsidio a ONG');

// -----------------------------------------------------------------------------
// Contactos de una obra
// -----------------------------------------------------------------------------
const contactoVacio = () => ({ contacto_id: 0, nombre: '', telefono: '', email: '' });

// Normaliza las tres formas de cg_12 a SIEMPRE un array:
//   - objeto unico  {"nombre":"a",...}      (154 obras, forma vieja)
//   - array         [{contacto_id, ...}, ...] (181 obras, forma nueva)
//   - texto plano   "Walter Gramajo"        (29 obras)
// Devuelve ademas el nombre de la forma, para avisarle al usuario si esto es
// un dato viejo y no un error.
export const parsearContactosObra = (valorCrudo) => {
    if (valorCrudo === null || valorCrudo === undefined) {
        return { contactos: [], forma: 'vacio' };
    }

    let v = valorCrudo;
    if (typeof v === 'string') {
        const bruto = v.trim();
        if (bruto === '') return { contactos: [], forma: 'vacio' };
        if (bruto[0] !== '[' && bruto[0] !== '{') {
            return { contactos: [{ ...contactoVacio(), nombre: bruto }], forma: 'texto' };
        }
        try {
            v = JSON.parse(bruto);
        } catch (e) {
            return { contactos: [{ ...contactoVacio(), nombre: bruto }], forma: 'texto' };
        }
    }

    const aArray = Array.isArray(v) ? v : [v];
    const contactos = aArray
        .filter((c) => c && typeof c === 'object')
        .map((c) => ({
            contacto_id: Number(c.contacto_id) || 0,
            nombre: (c.nombre ?? '').toString().trim(),
            telefono: (c.telefono ?? '').toString().trim(),
            email: (c.email ?? '').toString().trim()
        }))
        .filter((c) => c.nombre !== '' || c.telefono !== '' || c.email !== '');

    return {
        contactos,
        forma: Array.isArray(v) ? 'array' : 'objeto'
    };
};

// Clave para comparar contactos y no mostrar dos veces el mismo en la lista.
// Sin el id (contacto nuevo, todavia no guardado) compara por dato.
export const claveContacto = (c) => c.contacto_id
    ? `id:${c.contacto_id}`
    : `d:${normalizarTexto(c.nombre)}|${normalizarTexto(c.telefono)}|${normalizarTexto(c.email)}`;

// Como se ve un contacto en una linea.
export const contactoEnLinea = (c) => {
    const { nombre, canales } = partesContacto(c);
    if (nombre && canales.length) return `${nombre} — ${canales.join(' · ')}`;
    return nombre || canales.join(' · ') || '(sin datos)';
};

// Un email pelado no es un nombre. La migracion 061 dejo 4 contactos asi
// (nombre = "gramajow58@gmail.com"), porque venian de ongs.contacto, que era
// texto libre con canales mezclados.
export const pareceEmail = (texto) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((texto || '').trim());

// Separa el nombre de los canales, sin adivinar nada: si lo que hay en el
// campo nombre es un email, se devuelve como canal y el nombre queda vacio.
// Asi la UI nunca muestra un email como si fuera el nombre de alguien, y
// tampoco al reves: el nombre de una persona con su email al lado se lee
// como dos campos distintos.
export const partesContacto = (c) => {
    const crudo = (c.nombre || '').trim();
    const canales = [];
    let nombre = crudo;

    if (pareceEmail(crudo)) {
        canales.push(crudo);
        nombre = '';
    }
    if (c.telefono && c.telefono.trim()) canales.push(c.telefono.trim());
    if (c.email && c.email.trim()) canales.push(c.email.trim());

    return { nombre, canales };
};

// Quitar acentos y comparar, para el filtro de busqueda de la lista.
export const coincideContacto = (c, texto) => {
    const t = normalizarTexto(texto);
    if (t === '') return true;
    return normalizarTexto(c.nombre).includes(t)
        || normalizarTexto(c.telefono).includes(t)
        || normalizarTexto(c.email).includes(t);
};

// -----------------------------------------------------------------------------
// Aviso de obras con el dato congelado.
//
// Editar un contacto del catalogo NO cambia el cg_12 de las obras que lo usan:
// cada obra tiene una copia congelada y solo se actualiza cuando se vuelve a
// guardar ESA obra. Es a proposito (el historico no se reescribe), pero sin un
// aviso el usuario edita un telefono, no ve ningun cambio en la obra y piensa
// que la edicion no se guardo.
//
// El backend manda 'afectadas' con el contacto, el nombre y los codigos de obra
// que quedan con el dato viejo.
// -----------------------------------------------------------------------------

// "Ana Pérez (11 5555...): 114-015, 115-010 y 3 más" — una linea por contacto
// editado que alguna obra todavia usa con el dato anterior.
export const textoContactoAfectado = (afectado) => {
    const nombre = (afectado?.nombre || '').trim();
    const etiqueta = nombre !== '' ? nombre : `contacto ${afectado?.contacto_id ?? '?'}`;
    const obras = Array.isArray(afectado?.obras) ? afectado.obras : [];
    if (obras.length === 0) return etiqueta;
    // Con muchas obras la lista entera no entra: se muestran las primeras y el
    // resto como "y N más", que es como ya se listan los bloqueados.
    const detalle = obras.length > 4
        ? `${obras.slice(0, 4).join(', ')} y ${obras.length - 4} más`
        : obras.join(', ');
    return `${etiqueta}: ${detalle}`;
};

// -----------------------------------------------------------------------------
// Aviso de "esta obra muestra el dato viejo" (migracion 069).
//
// Es el hermano persistente del aviso de arriba: el modal sale al guardar el
// catalogo y dura lo que dura el modal. Esta es la fila que queda en la obra
// hasta que se vuelve a guardar ella, para que el que entre despues vea que el
// numero que tiene no es el del catalogo.
//
// El backend manda un objeto por contacto pendiente con nombre_anterior (lo que
// tiene la obra, del cg_12 guardado al marcar) y nombre_actual (lo que dice el
// catalogo).
// -----------------------------------------------------------------------------

// "En el catálogo figura 'araceLI cambio', distinto de lo que muestra esta obra.
//  Se actualiza al guardar la obra."
//
// Cuando los datos no cambiaron de verdad (por ejemplo solo cambio el formato de
// un telefono) no se dice nada: el aviso se marca solo con una edicion real.
export const textoContactoDesactualizado = (pendiente) => {
    if (!pendiente) return '';

    const act = (pendiente.nombre_actual ?? '').trim();
    const ant = (pendiente.nombre_anterior ?? '').trim();
    const telAct = (pendiente.telefono_actual ?? '').trim();
    const telAnt = (pendiente.telefono_anterior ?? '').trim();
    const mailAct = (pendiente.email_actual ?? '').trim();
    const mailAnt = (pendiente.email_anterior ?? '').trim();

    // El aviso es por contacto, no solo por nombre: si lo que cambio fue el
    // telefono o el mail (el nombre se escribio casi igual) tambien hay que
    // avisar. Comparar solo el nombre dejaba estos casos sin linea nenhuma, que
    // es el mismo "no se guardo" de siempre pero por otra via.
    const canales = [telAct, mailAct].filter(t => t !== '');
    const conCanales = canales.length > 0 ? ` (${canales.join(' · ')})` : '';

    const descripcion = act === '' ? '' : `"${act}"${conCanales}`;
    const contra = [ant, telAnt, mailAnt].filter(t => t !== '').join(' · ');

    // Que algo haya cambiado de verdad. Si no cambio nada no hay nada que
    // actualizar, asi que no hay ni linea ni boton.
    if (act === ant && telAct === telAnt && mailAct === mailAnt) return '';

    return 'En el catálogo de contactos figura '
        + (descripcion !== '' ? descripcion + ', ' : '')
        + 'distinto de lo que muestra esta obra'
        + (contra !== '' ? ` (${contra})` : '')
        + '.';
};
